import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { RichTextEditor } from "@/components/shared/RichTextEditor";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGuidanceRequestTemplate } from "@/hooks/use-app-settings";
import { useComplianceUsers, useRequestGuidance } from "@/hooks/use-audits";
import { useProject } from "@/hooks/use-projects";
import { useTemplates } from "@/hooks/use-templates";
import { renderGuidanceTemplate } from "@/lib/guidance-template";

interface RequestGuidanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string | undefined;
}

const schema = z.object({
  templateIds: z.array(z.string()).min(1, "At least one template is required"),
  assignedToId: z.string().min(1, "Auditor is required"),
  guidance: z
    .string()
    .trim()
    .min(1, "Guidance hours is required")
    .refine((v) => Number.isFinite(Number(v)) && Number(v) > 0, "Enter a positive number of hours"),
  message: z.string().trim().min(1, "Guidance request text is required"),
});

type FormValues = z.infer<typeof schema>;

export function RequestGuidanceDialog({
  open,
  onOpenChange,
  projectId,
}: RequestGuidanceDialogProps) {
  const { data: project } = useProject(projectId ?? "");
  const { data: templates } = useTemplates({ status: "active" });
  const { data: complianceUsers } = useComplianceUsers();
  const { data: messageTemplate } = useGuidanceRequestTemplate();
  const requestGuidance = useRequestGuidance();
  const [messageDirty, setMessageDirty] = useState(false);

  const auditors = (complianceUsers ?? []).filter(
    (u) => u.roles?.includes("compliance_auditor") || u.role === "compliance_auditor",
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      templateIds: [],
      assignedToId: "",
      guidance: "",
      message: "",
    },
  });

  const assignedToId = form.watch("assignedToId");
  const guidance = form.watch("guidance");
  const auditorName = auditors.find((u) => u.id === assignedToId)?.name ?? "";

  // Reset the form and re-arm auto-fill whenever the pop-up is (re)opened.
  useEffect(() => {
    if (open) {
      setMessageDirty(false);
      form.reset({ templateIds: [], assignedToId: "", guidance: "", message: "" });
    }
  }, [open, form]);

  // Keep the free-text body in sync with the template + current selections
  // until the user edits it directly — at that point their edits win.
  useEffect(() => {
    if (!open || messageDirty) return;
    const rendered = renderGuidanceTemplate(
      messageTemplate?.template ?? "",
      {
        project_name: project?.name ?? "",
        demand_name: project?.demand ?? "",
        auditor_name: auditorName,
        guidance,
      },
      { escapeHtml: true },
    );
    form.setValue("message", rendered);
  }, [
    open,
    messageDirty,
    messageTemplate,
    project?.name,
    project?.demand,
    auditorName,
    guidance,
    form,
  ]);

  async function onSubmit(values: FormValues) {
    if (!projectId) return;
    await requestGuidance.mutateAsync({
      projectId,
      projectName: project?.name ?? "",
      templateIds: values.templateIds,
      assignedToId: values.assignedToId,
      message: values.message,
      guidanceHours: Number(values.guidance),
    });
    form.reset();
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Request Guidance</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-1">
              <p className="text-sm font-medium leading-none">Project</p>
              <p className="flex h-9 w-full items-center rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground">
                {project?.name ?? projectId}
              </p>
            </div>
            <FormField
              control={form.control}
              name="templateIds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Templates</FormLabel>
                  <FormControl>
                    <div className="max-h-48 space-y-2 overflow-y-auto rounded-md border border-input p-3">
                      {(templates ?? []).length === 0 ? (
                        <p className="text-sm text-muted-foreground">No active templates</p>
                      ) : (
                        (templates ?? []).map((t) => (
                          <div key={t.id} className="flex items-center gap-3">
                            <Checkbox
                              id={`request-guidance-template-${t.id}`}
                              checked={field.value.includes(t.id)}
                              onCheckedChange={(checked) => {
                                field.onChange(
                                  checked
                                    ? [...field.value, t.id]
                                    : field.value.filter((id) => id !== t.id),
                                );
                              }}
                            />
                            <Label
                              htmlFor={`request-guidance-template-${t.id}`}
                              className="cursor-pointer font-normal"
                            >
                              {t.name}
                            </Label>
                          </div>
                        ))
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="assignedToId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Auditor</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an auditor" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {auditors.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="guidance"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Guidance (hours)</FormLabel>
                  <FormControl>
                    <Input type="number" min="0" step="0.5" placeholder="e.g. 2" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Guidance Request</FormLabel>
                  <FormControl>
                    <RichTextEditor
                      id={field.name}
                      ariaLabel="Guidance request"
                      value={field.value}
                      onChange={(html) => {
                        setMessageDirty(true);
                        field.onChange(html);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={requestGuidance.isPending}>
                {requestGuidance.isPending ? "Sending…" : "Send"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
